import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowDown as ArrowDownIcon,
  ArrowUp as ArrowUpIcon,
  Copy as CopyIcon,
  CheckCircle2 as CheckCircle2Icon,
  ClipboardList as ClipboardListIcon,
  Edit as EditIcon,
  Eye as EyeIcon,
  FileText as FileTextIcon,
  GripVertical as GripVerticalIcon,
  History as HistoryIcon,
  PauseCircle as PauseCircleIcon,
  Plus as PlusIcon,
  RefreshCcw as RefreshIcon,
  Search as SearchIcon,
  Sparkles as SparklesIcon,
  Trash2 as DeleteIcon
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const FALLBACK_CATEGORIES = [
  { value: 'difesa_giudiziale', label: 'Difesa Giudiziale' },
  { value: 'sovraindebitamento', label: 'Sovraindebitamento' },
  { value: 'stragiudiziale', label: 'Stragiudiziale' },
  { value: 'analisi_preliminare', label: 'Analisi Preliminare' }
];

const EMPTY_FORM = {
  nome: '',
  tipo_atto: '',
  categoria: '',
  modello_ai: null,
  attivo: true
};

const EMPTY_VERSION_FORM = {
  prompt_sistema: '',
  nota_modifica: '',
  documenti_snapshot: ''
};

const AI_STYLE_OPTIONS = [
  { value: 'standard', label: 'Standard professionale' },
  { value: 'prudente', label: 'Più prudente' },
  { value: 'giudiziale', label: 'Più giudiziale' },
  { value: 'negoziale', label: 'Più negoziale' },
  { value: 'tecnico', label: 'Più tecnico' }
];

const AI_SOURCE_USAGE_OPTIONS = [
  { value: 'replica_struttura', label: 'Replica struttura' },
  { value: 'replica_rigida', label: 'Replica rigida' },
  { value: 'adatta_migliora', label: 'Adatta e migliora' },
  { value: 'solo_spunti', label: 'Solo spunti' }
];

const AI_GENERATION_SUPPORTED_PROVIDERS = new Set(['openai', 'anthropic', 'google']);

const AI_ERROR_TAGS = {
  ai_missing_api_key: { label: 'Configurazione provider', color: 'warning' },
  ai_provider_unavailable: { label: 'Provider non pronto', color: 'warning' },
  ai_rate_limit: { label: 'Rate limit', color: 'warning' },
  ai_auth_error: { label: 'Credenziali provider', color: 'error' },
  ai_timeout: { label: 'Timeout', color: 'error' },
  ai_model_error: { label: 'Modello AI', color: 'error' },
  ai_invalid_response: { label: 'Risposta AI non valida', color: 'warning' },
  ai_generation_error: { label: 'Errore generazione', color: 'error' }
};

const AI_HELP_EXAMPLES = [
  {
    title: 'Opposizione decreto ingiuntivo su mutuo',
    prompt:
      'Voglio un template per opposizione a decreto ingiuntivo su mutuo ipotecario con focus su prova del credito, estratti conto, interessi di mora e richiesta sospensiva.',
    style: 'giudiziale',
    constraints:
      'Inserisci una sezione sulla prova del credito e una richiesta finale di sospensione della provvisoria esecutorieta.'
  },
  {
    title: 'Saldo e stralcio su credito ceduto',
    prompt:
      'Mi serve un template per proposta di saldo e stralcio su credito ceduto a societa veicolo o servicer, con taglio negoziale ma fermo e attenzione a saldo residuo, liberatoria e rinuncia al residuo.',
    style: 'negoziale',
    constraints:
      'Prevedi liberatoria finale, rinuncia al residuo e cancellazione di eventuali segnalazioni.'
  },
  {
    title: 'Richiesta ex art. 119 TUB',
    prompt:
      'Mi serve un template per richiesta documentazione bancaria ex art. 119 TUB relativa a mutuo ipotecario, con elenco puntuale dei documenti richiesti e riferimento alla necessita di ricostruire il rapporto.',
    style: 'tecnico',
    constraints:
      'Inserisci contratto, piano di ammortamento, quietanze, conteggio estintivo, estratti cronologici e atti di cessione.'
  },
  {
    title: 'Memoria integrativa OCC',
    prompt:
      'Vorrei un template per memoria integrativa in procedura di sovraindebitamento con focus su aggiornamento situazione debitoria, sopravvenienze reddituali, spese essenziali e documenti OCC da integrare.',
    style: 'standard',
    constraints:
      'Prevedi checklist finale dei documenti da integrare e sintesi della situazione economica aggiornata.'
  }
];

const AI_HELP_FORMULA =
  'Voglio un template per [tipo pratica o atto] con focus su [tema 1], [tema 2], [tema 3]. Prevedi [documenti o elementi obbligatori]. Il tono deve essere [piu tecnico / piu giudiziale / piu negoziale / piu prudente].';

const truncateHelpText = (value, maxLength = 140) => {
  const normalized = String(value || '').trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, maxLength).trim()}...`;
};

const normalizeProviderKey = (value) => String(value || '').trim().toLowerCase();

const filterAiModelOptions = (options, state) => {
  const term = String(state?.inputValue || '').trim().toLowerCase();
  if (!term) return options;
  return options.filter((option) => {
    const haystack = `${option?.nome_visualizzazione || ''} ${option?.nome_api || ''} ${option?.fornitore || ''}`.toLowerCase();
    return haystack.includes(term);
  });
};

const AI_HELP_GUIDELINES = [
  {
    title: 'Contesto della pratica',
    description: 'Indica chiaramente il tipo di rapporto o procedimento: mutuo, NPL, revolving, fideiussione, CRIF, sovraindebitamento.'
  },
  {
    title: 'Focus giuridici',
    description: 'Specifica 2 o 3 punti centrali da presidiare nella bozza, ad esempio prova del credito, interessi, legittimazione o sospensiva.'
  },
  {
    title: 'Documenti attesi',
    description: 'Se la bozza deve includere una checklist o documenti obbligatori, indicarlo esplicitamente migliora la qualità del risultato.'
  },
  {
    title: 'Registro del testo',
    description: 'Usa il campo stile per orientare il tono: tecnico, giudiziale, negoziale o prudente, in base all\'obiettivo del template.'
  }
];

const getAiProgressLabel = (progress) => {
  if (progress < 20) return 'Preparazione della richiesta';
  if (progress < 45) return 'Analisi del contesto giuridico';
  if (progress < 70) return 'Costruzione della struttura del template';
  if (progress < 90) return 'Rifinitura della bozza proposta';
  return 'Verifica finale della risposta AI';
};

const createTemporaryRowId = (suffix = 'row') => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${suffix}`;

const createEmptyDocumentRow = (ordine = 1) => ({
  id: createTemporaryRowId(ordine),
  nome: '',
  descrizione: '',
  obbligatorio: false,
  ordine
});

const cloneDocumentRow = (row, ordine = 1) => ({
  ...row,
  id: createTemporaryRowId(`copy-${ordine}`),
  ordine
});

const normalizeDocumentsOrder = (items = []) =>
  items.map((item, itemIndex) => ({
    ...item,
    ordine: itemIndex + 1
  }));

const formatDateTimeIt = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const StatWidget = ({ title, value, icon: Icon, color }) => (
  <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }} elevation={0} variant="outlined">
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function AttiAiTemplatePage() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    categorie: FALLBACK_CATEGORIES,
    creatori: [],
    modelliAi: []
  });
  const [stats, setStats] = useState({
    total_count: 0,
    active_count: 0,
    inactive_count: 0,
    with_version_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [attivoFilter, setAttivoFilter] = useState('all');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateDialogTab, setTemplateDialogTab] = useState('config');
  const [templateVersionsTab, setTemplateVersionsTab] = useState('history');
  const [templateSelectedVersion, setTemplateSelectedVersion] = useState(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentDeleteDialogOpen, setDocumentDeleteDialogOpen] = useState(false);
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
  const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [documentDeleteTarget, setDocumentDeleteTarget] = useState(null);
  const [selectedTemplateForVersions, setSelectedTemplateForVersions] = useState(null);
  const [selectedTemplateForDocuments, setSelectedTemplateForDocuments] = useState(null);
  const [versions, setVersions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [versionSaving, setVersionSaving] = useState(false);
  const [documentsSaving, setDocumentsSaving] = useState(false);
  const [draggedDocumentIndex, setDraggedDocumentIndex] = useState(null);
  const [dragOverDocumentIndex, setDragOverDocumentIndex] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [versionForm, setVersionForm] = useState(EMPTY_VERSION_FORM);
  const [aiRequest, setAiRequest] = useState('');
  const [pendingAiDraft, setPendingAiDraft] = useState(null);
  const [aiPreviewDraft, setAiPreviewDraft] = useState(null);
  const [aiGenerationModel, setAiGenerationModel] = useState(null);
  const [aiStyleGoal, setAiStyleGoal] = useState('standard');
  const [aiConstraints, setAiConstraints] = useState('');
  const [aiSourceModulistica, setAiSourceModulistica] = useState(null);
  const [aiSourceModulisticaUsage, setAiSourceModulisticaUsage] = useState('replica_struttura');
  const [aiSourceModulisticaOptions, setAiSourceModulisticaOptions] = useState([]);
  const [aiSourceModulisticaLoading, setAiSourceModulisticaLoading] = useState(false);
  const [aiSourceModulisticaInputValue, setAiSourceModulisticaInputValue] = useState('');
  const [aiDialogTab, setAiDialogTab] = useState('config');
  const [aiPreviewTab, setAiPreviewTab] = useState('summary');
  const [aiProgress, setAiProgress] = useState(0);
  const [aiExpandedHelpExample, setAiExpandedHelpExample] = useState(null);
  const [aiGenerationError, setAiGenerationError] = useState(null);
  const [aiShowTechnicalError, setAiShowTechnicalError] = useState(false);

  const categoryOptions = useMemo(
    () => (Array.isArray(meta.categorie) && meta.categorie.length > 0 ? meta.categorie : FALLBACK_CATEGORIES),
    [meta.categorie]
  );

  const creatorOptions = useMemo(() => meta.creatori || [], [meta.creatori]);
  const modelOptions = useMemo(() => meta.modelliAi || [], [meta.modelliAi]);
  const aiGenerationModelOptions = useMemo(
    () => modelOptions.filter((item) => AI_GENERATION_SUPPORTED_PROVIDERS.has(normalizeProviderKey(item?.fornitore))),
    [modelOptions]
  );
  const documentValidation = useMemo(
    () =>
      documents.map((item) => {
        const normalizedName = String(item?.nome || '').trim();
        return {
          nome: normalizedName,
          hasNameError: !normalizedName
        };
      }),
    [documents]
  );
  const invalidDocumentsCount = useMemo(
    () => documentValidation.filter((item) => item.hasNameError).length,
    [documentValidation]
  );
  const requiredDocumentsCount = useMemo(
    () => documents.filter((item) => Boolean(item?.obbligatorio)).length,
    [documents]
  );

  useEffect(() => {
    if (!aiGenerating) {
      setAiProgress((current) => (current >= 100 ? current : 0));
      return undefined;
    }

    setAiProgress(8);

    const intervalId = window.setInterval(() => {
      setAiProgress((current) => {
        if (current >= 94) {
          return current;
        }

        if (current < 20) {
          return Math.min(current + 6, 20);
        }
        if (current < 45) {
          return Math.min(current + 5, 45);
        }
        if (current < 70) {
          return Math.min(current + 4, 70);
        }
        if (current < 90) {
          return Math.min(current + 2, 90);
        }

        return Math.min(current + 1, 94);
      });
    }, 450);

    return () => window.clearInterval(intervalId);
  }, [aiGenerating]);

  useEffect(() => {
    if (!aiDialogOpen) return undefined;

    const trimmed = aiSourceModulisticaInputValue.trim();
    setAiSourceModulisticaLoading(true);

    const timerId = window.setTimeout(async () => {
      try {
        const response = await api.get('/modulistica', {
          params: {
            search: trimmed,
            limit: 20
          }
        });
        setAiSourceModulisticaOptions(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Errore lookup modulistica:', err);
        setAiSourceModulisticaOptions([]);
      } finally {
        setAiSourceModulisticaLoading(false);
      }
    }, 350);

    return () => window.clearTimeout(timerId);
  }, [aiDialogOpen, aiSourceModulisticaInputValue]);

  const fetchMeta = useCallback(async () => {
    try {
      const response = await api.get('/gtl-templates/meta');
      setMeta({
        categorie: response.data?.categorie || FALLBACK_CATEGORIES,
        creatori: response.data?.creatori || [],
        modelliAi: response.data?.modelliAi || []
      });
    } catch (err) {
      console.error('Errore caricamento meta template AI:', err);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        orderDirection: order
      };

      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (selectedCategory?.value) params.categoria = selectedCategory.value;
      if (selectedCreator?.id) params.creatoDa = selectedCreator.id;
      if (selectedModel?.id) params.modelloAi = selectedModel.id;
      if (attivoFilter !== 'all') params.attivo = attivoFilter;

      const response = await api.get('/gtl-templates', { params });

      setRows(response.data?.data || []);
      setTotalCount(Number(response.data?.meta?.totalCount || 0));
      setStats({
        total_count: Number(response.data?.stats?.total_count || 0),
        active_count: Number(response.data?.stats?.active_count || 0),
        inactive_count: Number(response.data?.stats?.inactive_count || 0),
        with_version_count: Number(response.data?.stats?.with_version_count || 0)
      });
      setError('');
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Errore nel caricamento dei template AI';
      console.error('Errore caricamento template AI:', err);
      setError(errorMessage);
      toast.error(errorMessage);

      // Se il cambio pagina fallisce, manteniamo i dati correnti e torniamo alla pagina precedente.
      if (page > 0) {
        setPage((currentPage) => (currentPage > 0 ? currentPage - 1 : 0));
      } else {
        setRows([]);
        setTotalCount(0);
        setStats({
          total_count: 0,
          active_count: 0,
          inactive_count: 0,
          with_version_count: 0
        });
      }
    } finally {
      setLoading(false);
    }
  }, [attivoFilter, order, orderBy, page, rowsPerPage, searchTerm, selectedCategory, selectedCreator, selectedModel]);

  const fetchVersions = useCallback(async (templateId) => {
    setVersionsLoading(true);
    try {
      const response = await api.get(`/gtl-templates/${templateId}/versions`);
      setSelectedTemplateForVersions(response.data?.template || null);
      setVersions(response.data?.data || []);
    } catch (err) {
      console.error('Errore caricamento versioni template AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nel caricamento delle versioni');
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  const fetchDocuments = useCallback(async (templateId) => {
    setDocumentsLoading(true);
    try {
      const response = await api.get(`/gtl-templates/${templateId}/documents`);
      setSelectedTemplateForDocuments(response.data?.template || null);
      setDocuments(response.data?.data || []);
      return response.data?.data || [];
    } catch (err) {
      console.error('Errore caricamento documenti template AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nel caricamento dei documenti');
      return [];
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleOpenCreate = () => {
    setEditingRow(null);
    setForm(EMPTY_FORM);
    setPendingAiDraft(null);
    setTemplateDialogTab('config');
    setTemplateVersionsTab('history');
    setTemplateSelectedVersion(null);
    setSelectedTemplateForVersions(null);
    setVersions([]);
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingRow(row);
    setPendingAiDraft(null);
    setTemplateDialogTab('config');
    setTemplateVersionsTab('history');
    setTemplateSelectedVersion(null);
    const selectedModelOption = modelOptions.find((item) => item.id === row.modello_ai_id) || null;
    setForm({
      nome: row.nome || '',
      tipo_atto: row.tipo_atto || '',
      categoria: row.categoria || '',
      modello_ai: selectedModelOption,
      attivo: Boolean(row.attivo)
    });
    setDialogOpen(true);
  };

  const handleOpenVersionsTab = async (row) => {
    setEditingRow(row);
    setPendingAiDraft(null);
    setTemplateDialogTab('versions');
    setTemplateVersionsTab('history');
    setTemplateSelectedVersion(null);
    const selectedModelOption = modelOptions.find((item) => item.id === row.modello_ai_id) || null;
    setForm({
      nome: row.nome || '',
      tipo_atto: row.tipo_atto || '',
      categoria: row.categoria || '',
      modello_ai: selectedModelOption,
      attivo: Boolean(row.attivo)
    });
    setSelectedTemplateForVersions(row);
    setVersions([]);
    setDialogOpen(true);
    await fetchVersions(row.id);
  };

  const handleOpenVersions = async (row) => {
    setSelectedTemplateForVersions(row);
    setVersions([]);
    setVersionsDialogOpen(true);
    await fetchVersions(row.id);
  };

  const handleOpenDocuments = async (row) => {
    setSelectedTemplateForDocuments(row);
    setDocuments([]);
    setDocumentsDialogOpen(true);
    await fetchDocuments(row.id);
  };

  const handleCloseDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingRow(null);
    setForm(EMPTY_FORM);
    setPendingAiDraft(null);
    setTemplateDialogTab('config');
    setTemplateVersionsTab('history');
    setTemplateSelectedVersion(null);
    setSelectedTemplateForVersions(null);
    setVersions([]);
  };

  const handleGenerateAiDraft = async () => {
    if (!aiRequest.trim()) {
      toast.error('Descrivi il template che vuoi far generare all\'AI');
      return;
    }

    if (aiGenerationModel?.id && !AI_GENERATION_SUPPORTED_PROVIDERS.has(normalizeProviderKey(aiGenerationModel?.fornitore))) {
      toast.error('Il modello selezionato non è compatibile con Genera con AI. Usa un modello OpenAI, Anthropic o Google.');
      return;
    }

    setAiGenerating(true);
    setAiGenerationError(null);
    setAiShowTechnicalError(false);
    setAiProgress(8);
    setAiDialogTab('config');
    setAiPreviewTab('summary');
    try {
      const response = await api.post('/gtl-templates/ai-draft', {
        request: aiRequest.trim(),
        preferredModelId: aiGenerationModel?.id || null,
        styleGoal: aiStyleGoal,
        constraints: aiConstraints.trim(),
        sourceModulisticaId: aiSourceModulistica?.id || null,
        sourceModulisticaUsage: aiSourceModulistica ? aiSourceModulisticaUsage : null
      });

      const draft = response.data?.draft;
      if (!draft) {
        toast.error('La bozza AI non è valida');
        return;
      }

      const selectedModelOption = modelOptions.find((item) => Number(item.id) === Number(draft.modello_ai_id)) || null;
      const normalizedDocuments = Array.isArray(draft.documenti)
        ? draft.documenti.map((item, index) => ({
            nome: item.nome || '',
            descrizione: item.descrizione || '',
            obbligatorio: Boolean(item.obbligatorio),
            ordine: index + 1
          }))
        : [];

      setAiPreviewDraft({
        nome: draft.nome || '',
        tipo_atto: draft.tipo_atto || '',
        categoria: draft.categoria || '',
        modello_ai: selectedModelOption,
        attivo: draft.attivo !== false,
        prompt_sistema: draft.prompt_sistema || '',
        struttura_atto: draft.struttura_atto || '',
        source_modulistica: draft.source_modulistica || null,
        nota_modifica: draft.nota_modifica || 'Prima versione generata con AI',
        documenti: normalizedDocuments
      });
      setAiGenerationError(null);
      setAiShowTechnicalError(false);
      setAiProgress(100);
      setAiDialogTab('preview');
      setAiPreviewTab('summary');
      toast.success('Bozza template generata con AI');
    } catch (err) {
      console.error('Errore generazione bozza template AI:', err);
      setAiProgress(0);
      const responseError = err?.response?.data || {};
      const message = responseError?.message || 'Errore nella generazione del template con AI';
      const detail = responseError?.detail || '';
      const suggestion = responseError?.suggestion || '';
      const code = responseError?.code || 'ai_generation_error';

      setAiGenerationError({
        code,
        message,
        detail,
        suggestion
      });
      setAiShowTechnicalError(false);

      toast.error(detail ? `${message}: ${detail}` : message);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleApplyAiHelpExample = (example) => {
    setAiRequest(example.prompt);
    setAiStyleGoal(example.style);
    setAiConstraints(example.constraints);
    setAiExpandedHelpExample(null);
    setAiGenerationError(null);
    setAiShowTechnicalError(false);
    setAiDialogTab('config');
    toast.success(`Esempio "${example.title}" applicato al form`);
  };

  const handleApplyAiDraft = () => {
    if (!aiPreviewDraft) {
      toast.error('Genera prima una bozza AI');
      return;
    }

    setEditingRow(null);
    setForm({
      nome: aiPreviewDraft.nome || '',
      tipo_atto: aiPreviewDraft.tipo_atto || '',
      categoria: aiPreviewDraft.categoria || '',
      modello_ai: aiPreviewDraft.modello_ai || null,
      attivo: aiPreviewDraft.attivo !== false
    });
    setPendingAiDraft({
      prompt_sistema: aiPreviewDraft.struttura_atto
        ? `${aiPreviewDraft.prompt_sistema || ''}\n\nStruttura suggerita dell'atto:\n${aiPreviewDraft.struttura_atto}`
        : aiPreviewDraft.prompt_sistema || '',
      nota_modifica: aiPreviewDraft.nota_modifica || 'Prima versione generata con AI',
      documenti: aiPreviewDraft.documenti || [],
      source_modulistica_id: aiPreviewDraft?.source_modulistica?.id || null,
      source_modulistica_usage: aiPreviewDraft?.source_modulistica?.usage || null,
      output_format: ['replica_struttura', 'replica_rigida'].includes(aiPreviewDraft?.source_modulistica?.usage) ? 'html' : 'markdown'
    });
    setAiDialogOpen(false);
    setAiRequest('');
    setAiPreviewDraft(null);
    setDialogOpen(true);
    toast.success('Bozza AI applicata al form');
  };

  const handleSave = async () => {
    if (!form.nome.trim() || !form.tipo_atto.trim() || !form.categoria || !form.modello_ai?.id) {
      toast.error('Compila tutti i campi obbligatori del template');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        tipo_atto: form.tipo_atto.trim(),
        categoria: form.categoria,
        modello_ai: form.modello_ai?.id || null,
        attivo: Boolean(form.attivo)
      };

      if (editingRow?.id) {
        await api.put(`/gtl-templates/${editingRow.id}`, payload);
        toast.success('Template AI aggiornato correttamente');
      } else {
        const createResponse = await api.post('/gtl-templates', payload);
        const createdTemplate = createResponse.data;

        if (pendingAiDraft?.prompt_sistema && createdTemplate?.id) {
          await api.post(`/gtl-templates/${createdTemplate.id}/versions`, {
            prompt_sistema: pendingAiDraft.prompt_sistema,
            nota_modifica: pendingAiDraft.nota_modifica || 'Prima versione generata con AI',
            documenti_snapshot: pendingAiDraft.documenti || [],
            source_modulistica_id: pendingAiDraft.source_modulistica_id || null,
            source_modulistica_usage: pendingAiDraft.source_modulistica_usage || null,
            output_format: pendingAiDraft.output_format || null
          });
        }

        if (Array.isArray(pendingAiDraft?.documenti) && pendingAiDraft.documenti.length > 0 && createdTemplate?.id) {
          await api.put(`/gtl-templates/${createdTemplate.id}/documents`, {
            documents: pendingAiDraft.documenti
          });
        }

        toast.success(
          pendingAiDraft?.prompt_sistema
            ? 'Template AI creato con versione iniziale e documenti'
            : 'Template AI creato correttamente'
        );
      }

      handleCloseDialog();
      fetchTemplates();
      fetchMeta();
    } catch (err) {
      console.error('Errore salvataggio template AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nel salvataggio del template AI');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;

    try {
      await api.delete(`/gtl-templates/${deleteTarget.id}`);
      toast.success('Template AI eliminato correttamente');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchTemplates();
      fetchMeta();
    } catch (err) {
      console.error('Errore eliminazione template AI:', err);
      toast.error(err?.response?.data?.message || 'Errore durante l\'eliminazione del template AI');
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedCategory(null);
    setSelectedCreator(null);
    setSelectedModel(null);
    setAttivoFilter('all');
    setPage(0);
  };

  const handleCreateVersion = async () => {
    if (!selectedTemplateForVersions?.id) return;
    if (!versionForm.prompt_sistema.trim()) {
      toast.error('Il prompt sistema è obbligatorio');
      return;
    }

    let parsedSnapshot;
    try {
      parsedSnapshot = JSON.parse(versionForm.documenti_snapshot || '[]');
    } catch (error) {
      toast.error('Il campo documenti snapshot deve contenere JSON valido');
      return;
    }

    setVersionSaving(true);
    try {
      await api.post(`/gtl-templates/${selectedTemplateForVersions.id}/versions`, {
        prompt_sistema: versionForm.prompt_sistema.trim(),
        nota_modifica: versionForm.nota_modifica.trim(),
        documenti_snapshot: parsedSnapshot
      });
      toast.success('Nuova versione creata correttamente');
      setNewVersionDialogOpen(false);
      setVersionForm(EMPTY_VERSION_FORM);
      await fetchVersions(selectedTemplateForVersions.id);
      await fetchTemplates();
    } catch (err) {
      console.error('Errore creazione versione template:', err);
      toast.error(err?.response?.data?.message || 'Errore nella creazione della versione');
    } finally {
      setVersionSaving(false);
    }
  };

  const handleSetCurrentVersion = async (version) => {
    if (!editingRow?.id || !version?.id) {
      return;
    }

    if (Number(editingRow.versione_corrente_id) === Number(version.id)) {
      toast.info('La versione selezionata è già corrente');
      return;
    }

    setVersionSaving(true);
    try {
      const response = await api.post(`/gtl-templates/${editingRow.id}/versions/${version.id}/set-current`);
      const updatedTemplate = response.data?.template || null;

      toast.success(response.data?.message || 'Versione corrente aggiornata');

      if (updatedTemplate) {
        setEditingRow(updatedTemplate);
        setSelectedTemplateForVersions(updatedTemplate);
      }

      await fetchVersions(editingRow.id);
      await fetchTemplates();
    } catch (err) {
      console.error('Errore impostazione versione corrente:', err);
      toast.error(err?.response?.data?.message || 'Errore nell\'impostazione della versione corrente');
    } finally {
      setVersionSaving(false);
    }
  };

  const handleOpenNewVersionDialog = async () => {
    if (!selectedTemplateForVersions?.id) return;
    const currentDocuments = await api
      .get(`/gtl-templates/${selectedTemplateForVersions.id}/documents`)
      .then((response) => response.data?.data || [])
      .catch(() => []);

    setVersionForm({
      ...EMPTY_VERSION_FORM,
      documenti_snapshot: JSON.stringify(currentDocuments, null, 2)
    });
    setNewVersionDialogOpen(true);
  };

  const handleAddDocument = () => {
    setDocuments((prev) => [...prev, createEmptyDocumentRow(prev.length + 1)]);
  };

  const handleUpdateDocument = (index, patch) => {
    setDocuments((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              ...patch
            }
          : item
      )
    );
  };

  const handleRemoveDocument = () => {
    if (documentDeleteTarget?.index == null) {
      return;
    }
    setDocuments((prev) =>
      normalizeDocumentsOrder(prev.filter((_, itemIndex) => itemIndex !== documentDeleteTarget.index))
    );
    setDocumentDeleteDialogOpen(false);
    setDocumentDeleteTarget(null);
  };

  const handleOpenDeleteDocumentDialog = (index) => {
    const selectedDocument = documents[index];
    if (!selectedDocument) {
      return;
    }
    setDocumentDeleteTarget({ ...selectedDocument, index });
    setDocumentDeleteDialogOpen(true);
  };

  const handleCloseDeleteDocumentDialog = () => {
    if (documentsSaving) {
      return;
    }
    setDocumentDeleteDialogOpen(false);
    setDocumentDeleteTarget(null);
  };

  const handleMoveDocument = (index, direction) => {
    setDocuments((prev) => {
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) {
        return prev;
      }
      const updated = [...prev];
      [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
      return normalizeDocumentsOrder(updated);
    });
  };

  const handleDuplicateDocument = (index) => {
    setDocuments((prev) => {
      const rowToDuplicate = prev[index];
      if (!rowToDuplicate) return prev;
      const duplicateRow = cloneDocumentRow(
        {
          ...rowToDuplicate,
          nome: rowToDuplicate.nome ? `${rowToDuplicate.nome} copia` : ''
        },
        index + 2
      );
      const updated = [...prev];
      updated.splice(index + 1, 0, duplicateRow);
      return normalizeDocumentsOrder(updated);
    });
  };

  const handleDocumentDragStart = (index) => {
    setDraggedDocumentIndex(index);
    setDragOverDocumentIndex(index);
  };

  const handleDocumentDragOver = (event, index) => {
    event.preventDefault();
    if (dragOverDocumentIndex !== index) {
      setDragOverDocumentIndex(index);
    }
  };

  const handleDocumentDrop = (dropIndex) => {
    if (draggedDocumentIndex === null || draggedDocumentIndex === dropIndex) {
      setDraggedDocumentIndex(null);
      setDragOverDocumentIndex(null);
      return;
    }

    setDocuments((prev) => {
      const updated = [...prev];
      const [movedRow] = updated.splice(draggedDocumentIndex, 1);
      updated.splice(dropIndex, 0, movedRow);
      return normalizeDocumentsOrder(updated);
    });
    setDraggedDocumentIndex(null);
    setDragOverDocumentIndex(null);
  };

  const handleDocumentDragEnd = () => {
    setDraggedDocumentIndex(null);
    setDragOverDocumentIndex(null);
  };

  const handleSaveDocuments = async () => {
    if (!selectedTemplateForDocuments?.id) return;

    const normalizedDocuments = documents.map((item, index) => ({
      nome: String(item.nome || '').trim(),
      descrizione: String(item.descrizione || '').trim(),
      obbligatorio: Boolean(item.obbligatorio),
      ordine: index + 1
    }));

    const firstInvalidIndex = normalizedDocuments.findIndex((item) => !item.nome);
    if (firstInvalidIndex >= 0) {
      toast.error(`Il nome del documento è obbligatorio alla riga ${firstInvalidIndex + 1}`);
      return;
    }

    setDocumentsSaving(true);
    try {
      await api.put(`/gtl-templates/${selectedTemplateForDocuments.id}/documents`, {
        documents: normalizedDocuments
      });
      toast.success('Documenti richiesti aggiornati correttamente');
      await fetchDocuments(selectedTemplateForDocuments.id);
    } catch (err) {
      console.error('Errore salvataggio documenti template AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nel salvataggio dei documenti');
    } finally {
      setDocumentsSaving(false);
    }
  };

  return (
    <Box className="p-6 space-y-6">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SparklesIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Template Atti AI
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione modelli base e storico versioni immutabile per la generazione atti AI.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<RefreshIcon size={16} />} onClick={fetchTemplates}>
            Ricarica
          </Button>
          <Button
            variant="outlined"
            startIcon={<SparklesIcon size={16} />}
            onClick={() => {
              setAiDialogTab('config');
              setAiPreviewTab('summary');
              setAiExpandedHelpExample(null);
              setAiSourceModulistica(null);
              setAiSourceModulisticaUsage('replica_struttura');
              setAiSourceModulisticaOptions([]);
              setAiSourceModulisticaInputValue('');
              setAiDialogOpen(true);
            }}
          >
            Genera con AI
          </Button>
          <Button variant="contained" startIcon={<PlusIcon size={16} />} onClick={handleOpenCreate}>
            Nuovo template
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 0 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget title="Template totali" value={stats.total_count} icon={FileTextIcon} color="#2563eb" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget title="Template attivi" value={stats.active_count} icon={CheckCircle2Icon} color="#16a34a" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget title="Template in bozza" value={stats.inactive_count} icon={PauseCircleIcon} color="#dc2626" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget title="Con versione" value={stats.with_version_count} icon={ClipboardListIcon} color="#7c3aed" />
        </Grid>
      </Grid>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <StyledCard>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cerca per nome, tipo atto, modello o creatore..."
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={16} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                options={categoryOptions}
                value={selectedCategory}
                onChange={(_, value) => {
                  setSelectedCategory(value);
                  setPage(0);
                }}
                getOptionLabel={(option) => option.label || option.value || ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => <TextField {...params} label="Categoria" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Stato"
                value={attivoFilter}
                onChange={(event) => {
                  setAttivoFilter(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="1">Attivi</MenuItem>
                <MenuItem value="0">In bozza</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                options={creatorOptions}
                value={selectedCreator}
                onChange={(_, value) => {
                  setSelectedCreator(value);
                  setPage(0);
                }}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Creato da" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                options={modelOptions}
                value={selectedModel}
                onChange={(_, value) => {
                  setSelectedModel(value);
                  setPage(0);
                }}
                getOptionLabel={(option) =>
                  option ? `${option.nome_visualizzazione}${option.fornitore ? ` · ${option.fornitore}` : ''}` : ''
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Modello AI" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1} sx={{ display: 'flex', alignItems: 'stretch' }}>
              <Button variant="outlined" fullWidth onClick={handleResetFilters}>
                Reset
              </Button>
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={orderBy === 'nome' ? order : false}>
                  <TableSortLabel active={orderBy === 'nome'} direction={orderBy === 'nome' ? order : 'asc'} onClick={() => handleRequestSort('nome')}>
                    Template
                  </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'tipo_atto' ? order : false}>
                  <TableSortLabel active={orderBy === 'tipo_atto'} direction={orderBy === 'tipo_atto' ? order : 'asc'} onClick={() => handleRequestSort('tipo_atto')}>
                    Tipo atto
                  </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'categoria' ? order : false}>
                  <TableSortLabel active={orderBy === 'categoria'} direction={orderBy === 'categoria' ? order : 'asc'} onClick={() => handleRequestSort('categoria')}>
                    Categoria
                  </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'modello_ai' ? order : false}>
                  <TableSortLabel active={orderBy === 'modello_ai'} direction={orderBy === 'modello_ai' ? order : 'asc'} onClick={() => handleRequestSort('modello_ai')}>
                    Modello AI
                  </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'versione_corrente_id' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'versione_corrente_id'}
                    direction={orderBy === 'versione_corrente_id' ? order : 'asc'}
                    onClick={() => handleRequestSort('versione_corrente_id')}
                  >
                    Versione
                  </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'attivo' ? order : false}>
                  <TableSortLabel active={orderBy === 'attivo'} direction={orderBy === 'attivo' ? order : 'asc'} onClick={() => handleRequestSort('attivo')}>
                    Stato
                  </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'creato_da_nome' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'creato_da_nome'}
                    direction={orderBy === 'creato_da_nome' ? order : 'asc'}
                    onClick={() => handleRequestSort('creato_da_nome')}
                  >
                    Creato da
                  </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'created_at' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? order : 'asc'}
                    onClick={() => handleRequestSort('created_at')}
                  >
                    Creato il
                  </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      Nessun template AI trovato con i filtri correnti.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow hover key={row.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.nome}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.tipo_atto}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={categoryOptions.find((item) => item.value === row.categoria)?.label || row.categoria}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.modello_ai_nome_visualizzazione || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.modello_ai_nome_api || ''}
                          {row.modello_ai_fornitore ? ` · ${row.modello_ai_fornitore}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.versione_corrente_numero ? (
                          <Chip size="small" color="secondary" variant="outlined" label={`V${row.versione_corrente_numero}`} />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={Number(row.attivo) === 1 ? 'success' : 'default'}
                          label={Number(row.attivo) === 1 ? 'Attivo' : 'Bozza'}
                          variant={Number(row.attivo) === 1 ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>{row.creato_da_nome || '-'}</TableCell>
                      <TableCell>{formatDateTimeIt(row.created_at)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title="Documenti richiesti">
                            <IconButton size="small" color="default" onClick={() => handleOpenDocuments(row)}>
                              <ClipboardListIcon size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Versioni">
                            <IconButton size="small" color="secondary" onClick={() => handleOpenVersionsTab(row)}>
                              <HistoryIcon size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Modifica">
                            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(row)}>
                              <EditIcon size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Elimina">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setDeleteTarget(row);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon size={18} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth={templateDialogTab === 'versions' ? 'lg' : 'md'}>
        <DialogTitle>
          {editingRow?.nome ? `Template AI - ${editingRow.nome}` : editingRow ? 'Modifica template AI' : 'Nuovo template AI'}
        </DialogTitle>
        <DialogContent dividers sx={{ px: 0, pt: 0, pb: 2 }}>
          <Tabs
            value={templateDialogTab}
            onChange={async (_event, value) => {
              setTemplateDialogTab(value);
              if (value === 'versions' && editingRow?.id) {
                setTemplateVersionsTab('history');
                setTemplateSelectedVersion(null);
                if (!selectedTemplateForVersions || Number(selectedTemplateForVersions.id) !== Number(editingRow.id)) {
                  setSelectedTemplateForVersions(editingRow);
                  setVersions([]);
                  await fetchVersions(editingRow.id);
                } else if (versions.length === 0 && !versionsLoading) {
                  await fetchVersions(editingRow.id);
                }
              }
            }}
            sx={{ px: 2.5, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="config" label="Configurazione" />
            <Tab value="versions" label={`Versioni (${editingRow?.versione_count || versions.length || 0})`} disabled={!editingRow?.id} />
          </Tabs>

          <Box sx={{ px: 3, pt: 2.5 }}>
            {templateDialogTab === 'config' ? (
              <>
                {!editingRow && pendingAiDraft ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Bozza AI pronta: al salvataggio verranno creati automaticamente anche la versione iniziale e
                    {` ${pendingAiDraft.documenti?.length || 0}`} documenti richiesti.
                  </Alert>
                ) : null}
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nome template"
                      value={form.nome}
                      onChange={(event) => setForm((prev) => ({ ...prev, nome: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Tipo atto"
                      value={form.tipo_atto}
                      onChange={(event) => setForm((prev) => ({ ...prev, tipo_atto: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Categoria"
                      value={form.categoria}
                      onChange={(event) => setForm((prev) => ({ ...prev, categoria: event.target.value }))}
                    >
                      {categoryOptions.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={modelOptions}
                      value={form.modello_ai}
                      onChange={(_, value) => setForm((prev) => ({ ...prev, modello_ai: value || null }))}
                      getOptionLabel={(option) =>
                        option ? `${option.nome_visualizzazione}${option.fornitore ? ` · ${option.fornitore}` : ''}` : ''
                      }
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Modello AI"
                          helperText={form.modello_ai?.nome_api || 'Seleziona un modello dal catalogo modelli_llm'}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Versione corrente"
                      value={editingRow?.versione_corrente_numero ? `V${editingRow.versione_corrente_numero}` : 'Nessuna versione'}
                      InputProps={{ readOnly: true }}
                      helperText="Gestita automaticamente dallo storico versioni"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(form.attivo)}
                          onChange={(event) => setForm((prev) => ({ ...prev, attivo: event.target.checked }))}
                        />
                      }
                      label="Template attivo e selezionabile"
                    />
                  </Grid>
                </Grid>
              </>
            ) : null}

            {templateDialogTab === 'versions' ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Ogni salvataggio crea una nuova versione immutabile. Il template punta sempre alla versione corrente.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                      Versione corrente:{' '}
                      <strong>
                        {editingRow?.versione_corrente_numero ? `V${editingRow.versione_corrente_numero}` : 'Nessuna versione'}
                      </strong>
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<PlusIcon size={16} />}
                    onClick={handleOpenNewVersionDialog}
                    disabled={!selectedTemplateForVersions?.id}
                  >
                    Nuova versione
                  </Button>
                </Box>

                <Tabs
                  value={templateVersionsTab}
                  onChange={(_event, value) => setTemplateVersionsTab(value)}
                  sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab value="history" label={`Storico versioni (${versions.length || 0})`} />
                  <Tab value="preview" label="Preview" disabled={!templateSelectedVersion} />
                </Tabs>

                {versionsLoading ? (
                  <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={26} />
                  </Box>
                ) : versions.length === 0 ? (
                  <Alert severity="info">Nessuna versione presente per questo template.</Alert>
                ) : templateVersionsTab === 'preview' && templateSelectedVersion ? (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Chip size="small" variant="outlined" label={`V${templateSelectedVersion.numero_versione || '-'}`} />
                        {editingRow?.versione_corrente_id === templateSelectedVersion.id ? (
                          <Chip size="small" color="success" variant="filled" label="Corrente" />
                        ) : null}
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${formatDateTimeIt(templateSelectedVersion.created_at)} · ${templateSelectedVersion.autore_nome || 'Utente non disponibile'}`}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {editingRow?.versione_corrente_id !== templateSelectedVersion.id ? (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={versionSaving}
                            onClick={() => handleSetCurrentVersion(templateSelectedVersion)}
                          >
                            Imposta come corrente
                          </Button>
                        ) : null}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CopyIcon size={14} />}
                          onClick={async () => {
                            try {
                              const content = String(templateSelectedVersion.prompt_sistema || '');
                              if (!content) {
                                toast.error('Nessun prompt da copiare');
                                return;
                              }
                              await navigator.clipboard.writeText(content);
                              toast.success('Prompt copiato');
                            } catch {
                              toast.error('Copia non disponibile');
                            }
                          }}
                        >
                          Copia prompt
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setVersionForm({
                              ...EMPTY_VERSION_FORM,
                              nota_modifica: `Derivata da V${templateSelectedVersion.numero_versione}`,
                              prompt_sistema: templateSelectedVersion.prompt_sistema || '',
                              documenti_snapshot: JSON.stringify(templateSelectedVersion.documenti_snapshot ?? [], null, 2)
                            });
                            setNewVersionDialogOpen(true);
                          }}
                        >
                          Apri come bozza
                        </Button>
                        <Button size="small" onClick={() => setTemplateVersionsTab('history')}>
                          Torna allo storico
                        </Button>
                      </Box>
                    </Box>

                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Nota modifica
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {templateSelectedVersion.nota_modifica || 'Nessuna nota modifica'}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={8}>
                        <TextField
                          label="Prompt sistema"
                          value={templateSelectedVersion.prompt_sistema || ''}
                          multiline
                          minRows={10}
                          fullWidth
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Documenti snapshot"
                          value={JSON.stringify(templateSelectedVersion.documenti_snapshot ?? [], null, 2)}
                          multiline
                          minRows={10}
                          fullWidth
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Creata il</TableCell>
                          <TableCell>Autore</TableCell>
                          <TableCell>Nota</TableCell>
                          <TableCell>Corrente</TableCell>
                          <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {versions.map((version) => {
                          const isCurrent = editingRow?.versione_corrente_id === version.id;
                          return (
                            <TableRow hover key={version.id}>
                              <TableCell>{version.numero_versione != null ? `V${version.numero_versione}` : '-'}</TableCell>
                              <TableCell>{formatDateTimeIt(version.created_at)}</TableCell>
                              <TableCell>{version.autore_nome || '-'}</TableCell>
                              <TableCell>{version.nota_modifica || '-'}</TableCell>
                              <TableCell>
                                {isCurrent ? <Chip size="small" color="success" label="Corrente" /> : <Chip size="small" variant="outlined" label="-" />}
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Apri preview">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      setTemplateSelectedVersion(version);
                                      setTemplateVersionsTab('preview');
                                    }}
                                  >
                                    <EyeIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Imposta come corrente">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={versionSaving || isCurrent}
                                    onClick={() => handleSetCurrentVersion(version)}
                                  >
                                    <CheckCircle2Icon size={16} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Copia prompt">
                                  <IconButton
                                    size="small"
                                    onClick={async () => {
                                      try {
                                        const content = String(version.prompt_sistema || '');
                                        if (!content) {
                                          toast.error('Nessun prompt da copiare');
                                          return;
                                        }
                                        await navigator.clipboard.writeText(content);
                                        toast.success('Prompt copiato');
                                      } catch {
                                        toast.error('Copia non disponibile');
                                      }
                                    }}
                                  >
                                    <CopyIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={handleCloseDialog} disabled={saving || documentsSaving || versionSaving}>
            Chiudi
          </Button>
          {templateDialogTab === 'config' ? (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvataggio...' : editingRow ? 'Salva modifiche' : 'Crea template'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={aiDialogOpen}
        onClose={() => {
          if (aiGenerating) return;
          setAiDialogOpen(false);
          setAiRequest('');
          setAiPreviewDraft(null);
          setAiStyleGoal('standard');
          setAiConstraints('');
          setAiSourceModulistica(null);
          setAiSourceModulisticaUsage('replica_struttura');
          setAiSourceModulisticaOptions([]);
          setAiSourceModulisticaInputValue('');
          setAiDialogTab('config');
          setAiPreviewTab('summary');
          setAiExpandedHelpExample(null);
          setAiGenerationError(null);
          setAiShowTechnicalError(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Genera template con AI</DialogTitle>
        <DialogContent dividers sx={{ px: 0, pt: 0, pb: 2 }}>
          {aiGenerating ? (
            <Box sx={{ px: 3, pt: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {getAiProgressLabel(aiProgress)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {aiProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={aiProgress}
                sx={{
                  height: 10,
                  borderRadius: 999,
                  bgcolor: 'primary.50',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 55%, #06b6d4 100%)'
                  }
                }}
              />
            </Box>
          ) : null}
          <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Descrivi il template che vuoi aggiungere. L&apos;AI proporrà nome, tipo atto, categoria, modello,
              prompt iniziale e documenti richiesti.
            </Typography>
            {aiGenerating ? (
              <Alert
                severity="info"
                icon={<CircularProgress size={18} color="inherit" />}
                sx={{ mt: 2 }}
              >
                L&apos;AI sta lavorando alla bozza del template. Avanzamento stimato: {aiProgress}%.
              </Alert>
            ) : null}
            {!aiGenerating && aiGenerationError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {aiGenerationError.message}
                  </Typography>
                  {aiGenerationError.code ? (
                    <Chip
                      size="small"
                      label={AI_ERROR_TAGS[aiGenerationError.code]?.label || 'Errore AI'}
                      color={AI_ERROR_TAGS[aiGenerationError.code]?.color || 'error'}
                      variant="filled"
                    />
                  ) : null}
                </Box>
                {aiGenerationError.suggestion ? (
                  <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>
                    {aiGenerationError.suggestion}
                  </Typography>
                ) : null}
                {aiGenerationError.detail ? (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      color="inherit"
                      onClick={() => setAiShowTechnicalError((current) => !current)}
                      sx={{ px: 0, minWidth: 0, fontWeight: 700, textTransform: 'none' }}
                    >
                      {aiShowTechnicalError ? 'Nascondi dettaglio tecnico' : 'Mostra dettaglio tecnico'}
                    </Button>
                    {aiShowTechnicalError ? (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1.25,
                          borderRadius: 1.5,
                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                          border: '1px solid',
                          borderColor: 'rgba(0, 0, 0, 0.08)'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {aiGenerationError.detail}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                ) : null}
              </Alert>
            ) : null}
          </Box>

          <Tabs
            value={aiDialogTab}
            onChange={(_event, value) => setAiDialogTab(value)}
            sx={{ px: 2.5, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="config" label="Configurazione" />
            <Tab
              value="help"
              label="Help prompt"
              icon={<SparklesIcon size={16} color="#7c3aed" />}
              iconPosition="start"
            />
            <Tab value="preview" label="Bozza proposta" disabled={!aiPreviewDraft && !aiGenerating} />
          </Tabs>

          <Box sx={{ px: 3, pt: 2.5 }}>
            {aiDialogTab === 'config' ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    label="Che template vuoi creare?"
                    placeholder="Es. Vorrei un template per opposizione a decreto ingiuntivo su mutuo ipotecario con focus su prova del credito, interessi di mora e richiesta sospensiva."
                    value={aiRequest}
                    onChange={(event) => setAiRequest(event.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Stile della bozza"
                    value={aiStyleGoal}
                    onChange={(event) => setAiStyleGoal(event.target.value)}
                    helperText="Guida il tono generale del template generato"
                  >
                    {AI_STYLE_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Vincoli / punti da includere"
                    placeholder="Es. Inserisci sempre richiesta sospensiva, focus su prova del credito, non usare tono aggressivo, prevedi documenti OCC."
                    value={aiConstraints}
                    onChange={(event) => setAiConstraints(event.target.value)}
                    helperText="Campo opzionale per guidare meglio la bozza AI"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={aiSourceModulisticaOptions}
                    value={aiSourceModulistica}
                    inputValue={aiSourceModulisticaInputValue}
                    loading={aiSourceModulisticaLoading}
                    openOnFocus
                    autoHighlight
                    onChange={(_event, value) => {
                      setAiSourceModulistica(value || null);
                      setAiSourceModulisticaUsage('replica_struttura');
                    }}
                    onInputChange={(_event, value) => setAiSourceModulisticaInputValue(value)}
                    filterOptions={(options) => options}
                    getOptionLabel={(option) => (option?.nome_modello ? String(option.nome_modello) : '')}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ py: 0.75 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {option.nome_modello}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                            {option.user_name ? (
                              <Chip size="small" variant="outlined" label={option.user_name} />
                            ) : null}
                            {option.categorie_nomi ? (
                              <Chip size="small" variant="outlined" label={option.categorie_nomi} />
                            ) : null}
                          </Box>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Documento di origine (Modulistica)"
                        placeholder="Cerca per nome modello, categoria o autore"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon size={18} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                        helperText={
                          aiSourceModulistica
                            ? 'Il contenuto verrà convertito da HTML a testo e usato come base per prompt e struttura atto'
                            : 'Opzionale: seleziona un modello documentale come base da integrare/migliorare con l’AI'
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Come usare il documento di origine"
                    value={aiSourceModulisticaUsage}
                    onChange={(event) => setAiSourceModulisticaUsage(event.target.value)}
                    disabled={!aiSourceModulistica}
                    helperText={
                      aiSourceModulistica
                        ? 'Definisce quanto la bozza deve seguire la struttura del modello Modulistica selezionato'
                        : 'Seleziona prima un documento di origine (Modulistica)'
                    }
                  >
                    {AI_SOURCE_USAGE_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={aiGenerationModelOptions}
                    value={aiGenerationModel}
                    openOnFocus
                    autoHighlight
                    onChange={(_, value) => setAiGenerationModel(value || null)}
                    filterOptions={filterAiModelOptions}
                    getOptionLabel={(option) =>
                      option ? `${option.nome_visualizzazione}${option.fornitore ? ` · ${option.fornitore}` : ''}` : ''
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ py: 0.75 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {option.nome_visualizzazione}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {option.fornitore ? `${option.fornitore}:${option.nome_api}` : option.nome_api}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Modello per la generazione"
                        placeholder="Cerca modello (nome o provider)"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon size={18} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                        helperText={
                          aiGenerationModel?.nome_api || 'Se lasci vuoto, il backend userà un modello compatibile disponibile e lo assegnerà anche alla bozza'
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
            ) : null}

            {aiDialogTab === 'help' ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 0,
                  borderRadius: 2,
                  overflow: 'hidden',
                  borderColor: 'divider',
                  bgcolor: 'background.paper'
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#fafbff'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: '#ede9fe',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SparklesIcon size={18} color="#7c3aed" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Guida alla scrittura del prompt
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Indicazioni operative ed esempi rapidi per ottenere bozze più precise e coerenti.
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ p: 2 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      bgcolor: '#fcfcfd',
                      borderStyle: 'dashed'
                    }}
                  >
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 0.6 }}>
                      Formula suggerita
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                      {AI_HELP_FORMULA}
                    </Typography>
                  </Paper>

                  <Box sx={{ mt: 2.25 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                      Criteri di compilazione
                    </Typography>
                    <Grid container spacing={1.25}>
                      {AI_HELP_GUIDELINES.map((item) => (
                        <Grid item xs={12} md={6} key={item.title}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              height: '100%',
                              bgcolor: '#ffffff'
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                              {item.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                              {item.description}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                </Box>

                  <Box sx={{ mt: 2.25 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                      Esempi pronti all'uso
                    </Typography>
                    <Grid container spacing={1.5}>
                      {AI_HELP_EXAMPLES.map((example) => (
                        <Grid item xs={12} md={6} key={example.title}>
                          {(() => {
                            const isExpanded = aiExpandedHelpExample === example.title;
                            return (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1.25,
                              bgcolor: '#ffffff'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {example.title}
                              </Typography>
                              <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={AI_STYLE_OPTIONS.find((item) => item.value === example.style)?.label || example.style}
                              />
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Prompt
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.65 }}>
                                {isExpanded ? example.prompt : truncateHelpText(example.prompt, 125)}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                p: 1.1,
                                borderRadius: 1.5,
                                bgcolor: '#f8fafc',
                                border: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                Vincolo consigliato
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.35, lineHeight: 1.6 }}>
                                {isExpanded ? example.constraints : truncateHelpText(example.constraints, 110)}
                              </Typography>
                            </Box>

                            <Box sx={{ pt: 0.25, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() =>
                                  setAiExpandedHelpExample((current) => (current === example.title ? null : example.title))
                                }
                              >
                                {isExpanded ? 'Comprimi' : 'Espandi'}
                              </Button>
                              <Button size="small" variant="outlined" onClick={() => handleApplyAiHelpExample(example)}>
                                Usa esempio
                              </Button>
                            </Box>
                          </Paper>
                            );
                          })()}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>
              </Paper>
            ) : null}

            {aiDialogTab === 'preview' ? (
              aiPreviewDraft ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 1.5 }}>
                    Bozza AI pronta. Puoi rigenerarla oppure applicarla al form di creazione.
                  </Alert>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Tabs
                      value={aiPreviewTab}
                      onChange={(_event, value) => setAiPreviewTab(value)}
                      sx={{ px: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}
                    >
                      <Tab value="summary" label="Riepilogo" />
                      <Tab value="prompt" label="Prompt iniziale" />
                      <Tab value="outline" label="Struttura atto" disabled={!aiPreviewDraft?.struttura_atto} />
                      <Tab value="documents" label="Documenti richiesti" />
                    </Tabs>

                    <Box sx={{ p: 2 }}>
                      {aiPreviewTab === 'summary' ? (
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                              Nome template
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {aiPreviewDraft.nome || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                              Tipo atto
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {aiPreviewDraft.tipo_atto || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">
                              Categoria
                            </Typography>
                            <Typography variant="body2">
                              {categoryOptions.find((item) => item.value === aiPreviewDraft.categoria)?.label || aiPreviewDraft.categoria || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">
                              Stile richiesto
                            </Typography>
                            <Typography variant="body2">
                              {AI_STYLE_OPTIONS.find((item) => item.value === aiStyleGoal)?.label || aiStyleGoal}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">
                              Modello assegnato
                            </Typography>
                          <Typography variant="body2">
                              {aiPreviewDraft.modello_ai
                                ? `${aiPreviewDraft.modello_ai.nome_visualizzazione}${aiPreviewDraft.modello_ai.fornitore ? ` · ${aiPreviewDraft.modello_ai.fornitore}` : ''}`
                                : '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={8}>
                            <Typography variant="caption" color="text.secondary">
                              Documento di origine
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {aiPreviewDraft?.source_modulistica?.nome_modello || '-'}
                              </Typography>
                              {aiPreviewDraft?.source_modulistica?.usage ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  label={
                                    AI_SOURCE_USAGE_OPTIONS.find((item) => item.value === aiPreviewDraft.source_modulistica.usage)?.label ||
                                    aiPreviewDraft.source_modulistica.usage
                                  }
                                />
                              ) : null}
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">
                              Documenti proposti
                            </Typography>
                            <Typography variant="body2">{aiPreviewDraft.documenti?.length || 0}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Vincoli richiesti
                            </Typography>
                            <Typography variant="body2">{aiConstraints.trim() || '-'}</Typography>
                          </Grid>
                          {aiPreviewDraft?.source_modulistica?.excerpt ? (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">
                                Estratto documento di origine
                              </Typography>
                              <Box
                                sx={{
                                  mt: 0.75,
                                  p: 1.5,
                                  bgcolor: 'grey.50',
                                  borderRadius: 1.5,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  maxHeight: 200,
                                  overflow: 'auto'
                                }}
                              >
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {aiPreviewDraft.source_modulistica.excerpt}
                                </Typography>
                              </Box>
                            </Grid>
                          ) : null}
                        </Grid>
                      ) : null}

                      {aiPreviewTab === 'prompt' ? (
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: 'grey.50',
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            maxHeight: 320,
                            overflow: 'auto'
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {aiPreviewDraft.prompt_sistema || '-'}
                          </Typography>
                        </Box>
                      ) : null}

                      {aiPreviewTab === 'outline' ? (
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: 'grey.50',
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            maxHeight: 320,
                            overflow: 'auto'
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {aiPreviewDraft.struttura_atto || '-'}
                          </Typography>
                        </Box>
                      ) : null}

                      {aiPreviewTab === 'documents' ? (
                        <Box>
                          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                            <Chip size="small" variant="outlined" label={`${aiPreviewDraft.documenti?.length || 0} documenti`} />
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`${(aiPreviewDraft.documenti || []).filter((item) => item?.obbligatorio).length} obbligatori`}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {(aiPreviewDraft.documenti || []).map((item, index) => (
                              <Chip
                                key={`${item.nome}-${index}`}
                                size="small"
                                color={item.obbligatorio ? 'warning' : 'default'}
                                variant={item.obbligatorio ? 'filled' : 'outlined'}
                                label={item.nome}
                              />
                            ))}
                          </Box>
                        </Box>
                      ) : null}
                    </Box>
                  </Paper>
                </Box>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{ p: 3, borderRadius: 2, textAlign: 'center', bgcolor: 'grey.50' }}
                >
                  <SparklesIcon size={28} />
                  <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                    Nessuna bozza disponibile
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Compila la configurazione e clicca `Genera bozza`. La proposta dell&apos;AI apparirà in questa tab.
                  </Typography>
                </Paper>
              )
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAiDialogOpen(false);
              setAiRequest('');
              setAiPreviewDraft(null);
              setAiStyleGoal('standard');
              setAiConstraints('');
              setAiDialogTab('config');
              setAiPreviewTab('summary');
              setAiExpandedHelpExample(null);
              setAiGenerationError(null);
              setAiShowTechnicalError(false);
              setAiSourceModulistica(null);
              setAiSourceModulisticaOptions([]);
              setAiSourceModulisticaInputValue('');
            }}
            disabled={aiGenerating}
          >
            Annulla
          </Button>
          <Button
            variant="outlined"
            onClick={handleGenerateAiDraft}
            disabled={aiGenerating}
            startIcon={aiGenerating ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {aiGenerating ? 'AI al lavoro...' : aiPreviewDraft ? 'Rigenera' : 'Genera bozza'}
          </Button>
          <Button variant="contained" onClick={handleApplyAiDraft} disabled={aiGenerating || !aiPreviewDraft}>
            Usa questa bozza
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={documentsDialogOpen}
        onClose={() => {
          if (documentsSaving) return;
          setDocumentsDialogOpen(false);
          setSelectedTemplateForDocuments(null);
          setDocuments([]);
          setDraggedDocumentIndex(null);
          setDragOverDocumentIndex(null);
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ px: 2.5, py: 1.75 }}>
          {selectedTemplateForDocuments?.nome
            ? `Documenti richiesti - ${selectedTemplateForDocuments.nome}`
            : 'Documenti richiesti'}
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3, fontSize: 13 }}>
                Elenco corrente dei documenti richiesti per il template. L'ordine definisce la sequenza mostrata in configurazione.
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                <Chip size="small" variant="outlined" label={`${documents.length} documenti`} />
                <Chip size="small" color="warning" variant="outlined" label={`${requiredDocumentsCount} obbligatori`} />
                <Chip
                  size="small"
                  color={invalidDocumentsCount > 0 ? 'error' : 'success'}
                  variant="outlined"
                  label={
                    invalidDocumentsCount > 0
                      ? `${invalidDocumentsCount} righe da completare`
                      : 'Validazione ok'
                  }
                />
              </Box>
            </Box>
            <Button variant="contained" size="small" startIcon={<PlusIcon size={15} />} onClick={handleAddDocument}>
              Aggiungi documento
            </Button>
          </Box>

          {documentsLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={26} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 32, py: 0.75, px: 0.5 }} />
                    <TableCell sx={{ width: 48, py: 0.75, px: 0.75 }}>#</TableCell>
                    <TableCell sx={{ width: '33%', py: 0.75, px: 0.75 }}>Nome</TableCell>
                    <TableCell sx={{ py: 0.75, px: 0.75 }}>Descrizione</TableCell>
                    <TableCell sx={{ width: 96, py: 0.75, px: 0.75 }}>Obbl.</TableCell>
                    <TableCell align="right" sx={{ width: 112, py: 0.75, px: 0.75 }}>
                      Azioni
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        Nessun documento configurato.
                      </TableCell>
                    </TableRow>
                  ) : (
                    documents.map((item, index) => (
                      <TableRow
                        key={item.id || `${item.nome}-${index}`}
                        hover
                        draggable
                        onDragStart={() => handleDocumentDragStart(index)}
                        onDragOver={(event) => handleDocumentDragOver(event, index)}
                        onDrop={() => handleDocumentDrop(index)}
                        onDragEnd={handleDocumentDragEnd}
                        sx={{
                          opacity: draggedDocumentIndex === index ? 0.6 : 1,
                          bgcolor: dragOverDocumentIndex === index ? 'rgba(79, 70, 229, 0.06)' : 'inherit',
                          '& .MuiTableCell-root': {
                            py: 0.5,
                            px: 0.75,
                            verticalAlign: 'middle'
                          }
                        }}
                      >
                        <TableCell sx={{ px: 0.5 }}>
                          <Tooltip title="Trascina per riordinare">
                            <IconButton size="small" sx={{ cursor: 'grab', p: 0.35 }}>
                              <GripVerticalIcon size={13} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={item.nome || ''}
                            onChange={(event) => handleUpdateDocument(index, { nome: event.target.value })}
                            placeholder="Es. Decreto ingiuntivo notificato"
                            error={documentValidation[index]?.hasNameError}
                            helperText={documentValidation[index]?.hasNameError ? 'Nome obbligatorio' : undefined}
                            FormHelperTextProps={{ sx: { mt: 0.25, lineHeight: 1.1 } }}
                            inputProps={{ style: { fontSize: 13, padding: '8px 10px' } }}
                            sx={{ '& .MuiOutlinedInput-root': { minHeight: 36 } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={item.descrizione || ''}
                            onChange={(event) => handleUpdateDocument(index, { descrizione: event.target.value })}
                            placeholder="Spiegazione breve del documento"
                            inputProps={{ style: { fontSize: 13, padding: '8px 10px' } }}
                            sx={{ '& .MuiOutlinedInput-root': { minHeight: 36 } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexWrap: 'nowrap' }}>
                            <Switch
                              checked={Boolean(item.obbligatorio)}
                              onChange={(event) => handleUpdateDocument(index, { obbligatorio: event.target.checked })}
                              size="small"
                              sx={{ mr: 0, ml: -0.5 }}
                            />
                            <Chip
                              size="small"
                              color={Boolean(item.obbligatorio) ? 'warning' : 'default'}
                              variant={Boolean(item.obbligatorio) ? 'filled' : 'outlined'}
                              label={Boolean(item.obbligatorio) ? 'Obbl.' : 'Fac.'}
                              sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.125 }}>
                            <Tooltip title="Sposta su">
                              <span>
                                <IconButton size="small" sx={{ p: 0.35 }} onClick={() => handleMoveDocument(index, 'up')} disabled={index === 0}>
                                  <ArrowUpIcon size={14} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Sposta giù">
                              <span>
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.35 }}
                                  onClick={() => handleMoveDocument(index, 'down')}
                                  disabled={index === documents.length - 1}
                                >
                                  <ArrowDownIcon size={14} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Duplica riga">
                              <IconButton size="small" sx={{ p: 0.35 }} color="primary" onClick={() => handleDuplicateDocument(index)}>
                                <CopyIcon size={14} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Elimina">
                              <IconButton
                                size="small"
                                sx={{ p: 0.35 }}
                                color="error"
                                onClick={() => handleOpenDeleteDocumentDialog(index)}
                              >
                                <DeleteIcon size={14} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            onClick={() => {
              setDocumentsDialogOpen(false);
              setSelectedTemplateForDocuments(null);
              setDocuments([]);
              setDraggedDocumentIndex(null);
              setDragOverDocumentIndex(null);
              setDocumentDeleteDialogOpen(false);
              setDocumentDeleteTarget(null);
            }}
            disabled={documentsSaving}
          >
            Chiudi
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDocuments}
            disabled={documentsSaving || documentsLoading || invalidDocumentsCount > 0}
          >
            {documentsSaving ? 'Salvataggio...' : 'Salva documenti'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={documentDeleteDialogOpen} onClose={handleCloseDeleteDocumentDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Elimina documento</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Stai per rimuovere il documento selezionato dalla configurazione del template.
          </Typography>
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50'
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Documento
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
              {documentDeleteTarget?.nome || '-'}
            </Typography>
            {documentDeleteTarget?.descrizione ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {documentDeleteTarget.descrizione}
              </Typography>
            ) : null}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            L&apos;operazione rimuove la riga dall&apos;elenco corrente dei documenti richiesti del template.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDocumentDialog} disabled={documentsSaving}>
            Annulla
          </Button>
          <Button color="error" variant="contained" onClick={handleRemoveDocument} disabled={documentsSaving}>
            Elimina documento
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={versionsDialogOpen}
        onClose={() => {
          setVersionsDialogOpen(false);
          setSelectedTemplateForVersions(null);
          setVersions([]);
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {selectedTemplateForVersions?.nome
            ? `Storico versioni - ${selectedTemplateForVersions.nome}`
            : 'Storico versioni'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Ogni salvataggio crea una nuova versione immutabile. Il template punta sempre alla versione corrente.
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75 }}>
                Versione corrente:{' '}
                <strong>
                  {selectedTemplateForVersions?.versione_corrente_numero
                    ? `V${selectedTemplateForVersions.versione_corrente_numero}`
                    : 'Nessuna versione'}
                </strong>
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<PlusIcon size={16} />}
              onClick={handleOpenNewVersionDialog}
              disabled={!selectedTemplateForVersions?.id}
            >
              Nuova versione
            </Button>
          </Box>

          {versionsLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={26} />
            </Box>
          ) : versions.length === 0 ? (
            <Alert severity="info">Nessuna versione presente per questo template.</Alert>
          ) : (
            <Box sx={{ display: 'grid', gap: 2 }}>
              {versions.map((version) => {
                const isCurrent = selectedTemplateForVersions?.versione_corrente_id === version.id;
                return (
                  <Card key={version.id} variant="outlined" sx={{ borderRadius: 3 }}>
                    <Box sx={{ p: 2.25 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={`V${version.numero_versione}`} color={isCurrent ? 'secondary' : 'default'} size="small" />
                          {isCurrent ? <Chip label="Corrente" color="success" size="small" /> : null}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTimeIt(version.created_at)} · {version.autore_nome || 'Utente non disponibile'}
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Nota modifica
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {version.nota_modifica || 'Nessuna nota modifica'}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                          <TextField
                            label="Prompt sistema"
                            value={version.prompt_sistema || ''}
                            multiline
                            minRows={6}
                            fullWidth
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Documenti snapshot"
                            value={JSON.stringify(version.documenti_snapshot ?? [], null, 2)}
                            multiline
                            minRows={6}
                            fullWidth
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setVersionsDialogOpen(false);
              setSelectedTemplateForVersions(null);
              setVersions([]);
            }}
          >
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newVersionDialogOpen} onClose={() => !versionSaving && setNewVersionDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Nuova versione template</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nota modifica"
                value={versionForm.nota_modifica}
                onChange={(event) => setVersionForm((prev) => ({ ...prev, nota_modifica: event.target.value }))}
                placeholder="Es. Aggiunta eccezione prescrizione"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={8}
                label="Prompt sistema"
                value={versionForm.prompt_sistema}
                onChange={(event) => setVersionForm((prev) => ({ ...prev, prompt_sistema: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Documenti snapshot (JSON)"
                value={versionForm.documenti_snapshot}
                onChange={(event) => setVersionForm((prev) => ({ ...prev, documenti_snapshot: event.target.value }))}
                helperText="Inserisci un array o oggetto JSON valido con i documenti richiesti per questa versione"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewVersionDialogOpen(false)} disabled={versionSaving}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleCreateVersion} disabled={versionSaving}>
            {versionSaving ? 'Salvataggio...' : 'Crea versione'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Vuoi eliminare il template <strong>{deleteTarget?.nome || '-'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
            }}
          >
            Annulla
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
